/**
 * The GraphQL client provider (Cedar removal, Stage 4.1b): what
 * @cedarjs/web 5.0.5's `RedwoodApolloProvider` (apollo/index.js with
 * FetchConfigProvider.js) did for this app, on `@apollo/client` directly.
 *
 * Kept as it was: one `getToken()` per operation (`setContext`), then the
 * auth headers on the request: `auth-provider` alone when the user is
 * authenticated but the adapter has no token at that moment, both
 * `auth-provider` and `authorization: Bearer <token>` when there is a token,
 * neither otherwise; the app's static link headers (`x-milano-id`) on every
 * request; the terminating link at `GRAPHQL_URL` with no `credentials`
 * option; `defaultOptions.watchQuery` `cache-and-network` +
 * `notifyOnNetworkStatusChange`; Apollo's log verbosity at `debug`; one
 * `InMemoryCache` for the provider's lifetime and a client rebuilt on every
 * render of the inner component (Cedar's shape; memoizing the client is a
 * recorded follow-up, not a parity item); the cache config passed through.
 *
 * Dropped, with no user here: the upload link (the api has no File scalar;
 * for JSON operations Apollo's `HttpLink` sends the same POST, method, URL,
 * headers and body key order, with one difference: `HttpLink` omits
 * variables the document does not reference (`includeUnusedVariables`
 * defaults to false) where Cedar's upload link sent every key a Cell's props
 * carried, e.g. App2Cell's stray `applicantId`; the api ignores undeclared
 * variables, so nothing changes server-side and less rides on the wire), the
 * persisted-query split (no document carries a hash), the SSE link (no
 * subscriptions), the fragment registry, the `mostRecentRequest` annotation
 * (its reader was Cedar's dev error page) and the context hooks provider.
 */
import React, { useRef } from 'react'
import type { ReactNode } from 'react'

import {
  ApolloClient,
  ApolloLink,
  HttpLink,
  InMemoryCache,
  setLogVerbosity,
} from '@apollo/client'
import { ApolloProvider } from '@apollo/client/react'
import type { InMemoryCacheConfig } from '@apollo/client'
import { setContext } from '@apollo/client/link/context'

import type { AuthContextValue } from 'src/platform/auth/types'
import { GRAPHQL_URL } from 'src/platform/config'

// Cedar set this on every provider render with its default of 'debug'.
setLogVerbosity('debug')

type AuthForApollo = Pick<
  AuthContextValue,
  'getToken' | 'type' | 'isAuthenticated'
>

/** The link chain: token lookup, auth headers, then the HTTP transport. */
export const createLink = (
  auth: AuthForApollo,
  headers: Record<string, string> | undefined
) => {
  const withToken = setContext(async () => ({ token: await auth.getToken() }))
  const authMiddleware = new ApolloLink((operation, forward) => {
    const { token } = operation.getContext() as { token?: string | null }
    const providerHeader = auth.isAuthenticated
      ? { 'auth-provider': auth.type }
      : {}
    const authHeaders = token
      ? { 'auth-provider': auth.type, authorization: `Bearer ${token}` }
      : {}
    operation.setContext(() => ({
      headers: {
        ...(operation.getContext().headers as Record<string, string>),
        ...providerHeader,
        ...authHeaders,
      },
    }))
    return forward(operation)
  })
  return ApolloLink.from([
    withToken,
    authMiddleware,
    new HttpLink({ uri: GRAPHQL_URL, headers }),
  ])
}

export const createClient = (
  auth: AuthForApollo,
  cache: InMemoryCache,
  headers: Record<string, string> | undefined
) =>
  new ApolloClient({
    defaultOptions: {
      watchQuery: {
        fetchPolicy: 'cache-and-network',
        notifyOnNetworkStatusChange: true,
      },
    },
    link: createLink(auth, headers),
    cache,
  })

export interface GraphQLProviderProps {
  useAuth: () => AuthContextValue
  /** Static headers on every request (the app passes `x-milano-id`). */
  headers?: Record<string, string>
  cacheConfig?: InMemoryCacheConfig
  children?: ReactNode
}

const ClientProvider = ({
  useAuth,
  headers,
  cache,
  children,
}: Omit<GraphQLProviderProps, 'cacheConfig'> & { cache: InMemoryCache }) => {
  const { getToken, type, isAuthenticated } = useAuth()
  const client = createClient(
    { getToken, type, isAuthenticated },
    cache,
    headers
  )
  return <ApolloProvider client={client}>{children}</ApolloProvider>
}

export const GraphQLProvider = ({
  useAuth,
  headers,
  cacheConfig,
  children,
}: GraphQLProviderProps) => {
  const cache = useRef<InMemoryCache | null>(null)
  cache.current ??= new InMemoryCache(cacheConfig)
  return (
    <ClientProvider useAuth={useAuth} headers={headers} cache={cache.current}>
      {children}
    </ClientProvider>
  )
}
